import { useMemo, useState } from 'react';
import { formatMoney } from '@dill/dill-shared/dist/src';

export interface SalesRepSummary {
  name: string;
  submitted: number;
  approved: number;
  rejected: number;
  approvalRate: number;
  averageCreditApproved: number;
}

interface SalesRepBreakdownTableProps {
  data: SalesRepSummary[];
}

type SortKey =
  | 'name'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'approvalRate'
  | 'averageCreditApproved';

function SalesRepBreakdownTable({ data }: SalesRepBreakdownTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('submitted');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [data, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col)
      return (
        <svg
          className="w-3 h-3 text-gray-300 ml-1 inline"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    return sortDir === 'desc' ? (
      <svg
        className="w-3 h-3 text-blue-500 ml-1 inline"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 text-blue-500 ml-1 inline"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
        No sales rep data available
      </div>
    );
  }

  const cols: { key: SortKey; label: string; align: 'left' | 'right' }[] = [
    { key: 'name', label: 'Sales Rep', align: 'left' },
    { key: 'submitted', label: 'Submitted', align: 'right' },
    { key: 'approved', label: 'Approved', align: 'right' },
    { key: 'rejected', label: 'Rejected', align: 'right' },
    { key: 'approvalRate', label: 'Approval Rate', align: 'right' },
    { key: 'averageCreditApproved', label: 'Avg Credit Approved', align: 'right' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            {cols.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 ${
                  col.align === 'right' ? 'text-right' : 'text-left'
                }`}
                onClick={() => handleSort(col.key)}>
                {col.label}
                <SortIcon col={col.key} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.map((row) => (
            <tr key={row.name} className="hover:bg-gray-50">
              <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                {row.name}
              </td>
              <td className="px-3 py-2 text-sm text-gray-700 text-right">{row.submitted}</td>
              <td className="px-3 py-2 text-sm text-green-600 font-medium text-right">
                {row.approved}
              </td>
              <td className="px-3 py-2 text-sm text-red-500 text-right">{row.rejected}</td>
              <td className="px-3 py-2 text-right">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    row.approvalRate >= 0.7
                      ? 'bg-green-100 text-green-700'
                      : row.approvalRate >= 0.4
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                  {(row.approvalRate * 100).toFixed(0)}%
                </span>
              </td>
              <td className="px-3 py-2 text-sm text-gray-700 text-right">
                {row.averageCreditApproved > 0 ? formatMoney(row.averageCreditApproved, 2) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SalesRepBreakdownTable;
